import React from 'react';
import {
  PageHeader,
  DataTable,
  Button,
  Input,
  Label,
  type Column,
} from '@redbax/ui-kit';
import { Plus, Trash2 } from 'lucide-react';
import {
  useAdminPlatformAdmins,
  useGrantPlatformAdmin,
  useRevokePlatformAdmin,
} from '../api/admin-hooks';
import type { PlatformAdmin } from '../types/admin-types';
import { formatDate } from '@redbax/shared';

export function AdminPlatformAdminsPage() {
  const { data: admins, isLoading } = useAdminPlatformAdmins();
  const grantAdmin = useGrantPlatformAdmin();
  const revokeAdmin = useRevokePlatformAdmin();

  const [showAddDialog, setShowAddDialog] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState('');
  const [confirmRemoveId, setConfirmRemoveId] = React.useState<string | null>(null);

  const handleGrant = () => {
    if (!newEmail.trim()) return;
    grantAdmin.mutate({ email: newEmail.trim() });
    setNewEmail('');
    setShowAddDialog(false);
  };

  const handleRevoke = () => {
    if (!confirmRemoveId) return;
    revokeAdmin.mutate({ id: confirmRemoveId });
    setConfirmRemoveId(null);
  };

  const adminToRemove = admins?.find((a) => a.id === confirmRemoveId);

  const columns: Column<PlatformAdmin>[] = [
    { key: 'email', header: 'Email', sortable: true },
    { key: 'displayName', header: 'Display Name', sortable: true },
    {
      key: 'grantedAt',
      header: 'Granted At',
      sortable: true,
      render: (row) => formatDate(row.grantedAt),
    },
    {
      key: 'actions' as keyof PlatformAdmin,
      header: '',
      render: (row) => (
        <Button size="sm" variant="ghost" onClick={() => setConfirmRemoveId(row.id)}>
          <Trash2 className="h-4 w-4 text-red-accent" />
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Platform Admins"
        description="Manage platform-level administrators"
        actions={
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Admin
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={admins ?? []}
        keyExtractor={(a) => a.id}
        loading={isLoading}
        emptyMessage="No platform admins found"
      />

      {/* Add Admin Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/80" onClick={() => setShowAddDialog(false)} />
          <div className="relative z-50 w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-text-primary">Add Platform Admin</h3>
            <div className="mt-4 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email Address</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="admin@example.com"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleGrant} disabled={!newEmail.trim()}>
                Grant Admin
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Remove Dialog */}
      {confirmRemoveId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/80" onClick={() => setConfirmRemoveId(null)} />
          <div className="relative z-50 w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-text-primary">Remove Admin</h3>
            <p className="mt-2 text-sm text-text-secondary">
              Are you sure you want to remove <strong>{adminToRemove?.email}</strong> as a platform admin?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmRemoveId(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleRevoke}>
                Remove
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
